// layouts/certificaciones/nueva-certificacion.js
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import Alert from "@mui/material/Alert";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import TextField from "@mui/material/TextField";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";

import FormPage from "ui/FormPage";
import { PrimaryButton, OutlineButton } from "ui/Buttons";

import {
  createCertificacion,
  listFormatos,
  listGeneros,
  listRecordTypes,
  listGrupos,
  listPerformers,
  listSellos,
  toCertFormData,     // multipart FormData
} from "./services/certificaciones";
import { getCountriesES } from "utils/countries";
import { useAuth } from "layouts/auth/AuthContext";
import api from "services/api";

/* ---------------- config ---------------- */
const MAX_FILE_MB = 20;
const MAX_FILE_BYTES = MAX_FILE_MB * 1024 * 1024;

/* ------------ helpers ------------ */
// Convierte a número solo si es válido (evita "" -> 0 y NaN)
const toNum = (v) => {
  if (v === "" || v === null || v === undefined) return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
};

// Convierte arreglos de ids (strings) a números válidos
const toNumArray = (arr) =>
  Array.isArray(arr)
    ? arr
        .map((x) => Number(x))
        .filter((n) => Number.isFinite(n))
    : undefined;

const FRIENDLY_MAP = [
  {
    test: /empty award/i,
    msg: "Las unidades no corresponden para alguna certificación, favor de revisar",
  },
  {
    test: /artist.*required|artist.*obligatorio/i,
    msg: "El artista es obligatorio.",
  },
  {
    test: /title.*required|title.*obligatorio/i,
    msg: "El título es obligatorio.",
  },
  {
    test: /release[_ ]?date.*required|release[_ ]?date.*obligatoria/i,
    msg: "La fecha de lanzamiento es obligatoria.",
  },
  {
    test: /post[_ ]?at.*required|post[_ ]?at.*obligatoria/i,
    msg: "La fecha de publicación (post_at) es obligatoria.",
  },
  {
    test: /nationality.*(invalid|must be)/i,
    msg: "La nacionalidad debe ser un código ISO válido (ej. MX, US).",
  },
  {
    test: /sale[_ ]?units.*must be greater than 0/i,
    msg: "Las unidades vendidas deben ser mayores a 0.",
  },
  {
    test: /duplicate|already exists|unique/i,
    msg: "Ya existe una certificación con datos similares (posible duplicado).",
  },
  {
    test: /cannot destructure property ['"]label['"] of ['"]req\.files['"] as it is null|req\.files.*null/i,
    msg: "Antes de guardar, adjunta los 3 archivos solicitados: sello, reporte y portada.",
  },
  {
    test: /label.*required|report.*required|cover.*required|faltan.*archivos|missing.*files/i,
    msg: "Faltan archivos por adjuntar. Carga sello, reporte y portada para poder guardar.",
  },
];

const safeMsg = (x) => {
  try {
    if (x == null) return "";
    if (typeof x === "string") return x;
    if (typeof x === "number" || typeof x === "boolean") return String(x);
    if (Array.isArray(x)) return x.map(safeMsg).join(", ");
    if (typeof x === "object") {
      if (typeof x.message === "string") return x.message;
      if (x.instancePath || x.schemaPath || x.keyword) {
        const path = String(x.instancePath || "").replace(/^\//, "").replace(/\//g, ".");
        return path ? `${path}: ${x.message || "error"}` : (x.message || "error");
      }
      return JSON.stringify(x);
    }
    return String(x);
  } catch {
    return String(x);
  }
};

const translateMsg = (raw) => {
  const s = safeMsg(raw);
  if (!s) return "";
  for (const rule of FRIENDLY_MAP) {
    if (rule.test.test(s)) return rule.msg;
  }
  return s; // fallback: mensaje tal cual manda el backend
};

const parseErr = (e) => {
  const out = [];
  const res = e?.response;
  const d = res?.data ?? e;

  const push = (m) => {
    if (!m && m !== 0) return;

    // primitivos
    if (typeof m === "string" || typeof m === "number" || typeof m === "boolean") {
      out.push(translateMsg(m));
      return;
    }

    // arrays
    if (Array.isArray(m)) {
      m.forEach(push);
      return;
    }

    // objetos
    if (typeof m === "object") {
      if (Array.isArray(m.errors))  push(m.errors);
      if (Array.isArray(m.issues))  push(m.issues);
      if (Array.isArray(m.details)) push(m.details);
      if (Array.isArray(m.message)) push(m.message);

      // formato tipo { message: "...", path: "...", instancePath: "..." }
      if (m.instancePath || m.path || m.dataPath) {
        const path = String(m.instancePath || m.path || m.dataPath || "")
          .replace(/^\//, "")
          .replace(/\//g, ".");
        const msg = m.message || m.msg || m.error || "error";
        const full = path ? `${path}: ${msg}` : msg;
        out.push(translateMsg(full));
        return;
      }

      if (m.message || m.msg || m.error) {
        out.push(translateMsg(m.message || m.msg || m.error));
        return;
      }

      // último recurso: serializar el objeto
      out.push(translateMsg(m));
      return;
    }

    // fallback
    out.push(translateMsg(m));
  };

  push(d);

  // agrega info de HTTP si aplica
  if (res?.status) {
    if (res.status === 413) {
      out.push("El archivo enviado es demasiado grande. Intenta comprimirlo o usar una versión más ligera.");
    } else if (res.status >= 500) {
      const hasFriendlyFilesMsg = out.some((msg) => /adjunta los 3 archivos solicitados|Faltan archivos por adjuntar/i.test(String(msg || "")));
      if (!hasFriendlyFilesMsg) {
        out.push(`Error del servidor (HTTP ${res.status}). Intenta más tarde.`);
      }
    } else if (!out.length) {
      // Solo mostramos el código HTTP si no hubo ningún mensaje útil antes
      out.push(`HTTP ${res.status}`);
    }
  }


  // limpiar, deduplicar
  return [...new Set(out)].map(safeMsg).filter(Boolean);
};

const ellipsis = (w = 220) => ({
  maxWidth: w,
  display: "inline-block",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
});

const isoDate = (v) => (v ? String(v).slice(0, 10) : undefined);

const isSafariBrowser = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /Safari/i.test(ua) && !/Chrome|Chromium|Android/i.test(ua);
};

const isValidYMD = (v = "") => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) return false;
  const d = new Date(`${v}T00:00:00`);
  if (Number.isNaN(d.getTime())) return false;
  return d.toISOString().slice(0, 10) === v;
};


const isAdminUser = (u) =>
  u?.is_admin === true || u?.is_admin === 1 || String(u?.is_admin) === "1";

const namesFromIds = (ids = [], options = []) =>
  (Array.isArray(ids) ? ids : [])
    .map((id) => options.find((o) => String(o.id) === String(id))?.name)
    .filter(Boolean);


// ===== Código helpers =====
const normalizeText = (s = "") =>
  String(s).normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();

// Normaliza ISRC: quita "ISRC", espacios, guiones y deja solo A-Z0-9 en mayúsculas
const normalizeISRC = (raw = "") => {
  const s = String(raw).toUpperCase().trim();
  const noPrefix = s.replace(/^ISRC\s*/i, "");
  const alnum = noPrefix.replace(/[^A-Z0-9]/g, "");
  return alnum;
};

// Valida estructura: CC + XXX + YY + NNNNN  (12 chars total)
const isValidISRC = (raw = "") => {
  const v = normalizeISRC(raw);
  return /^[A-Z]{2}[A-Z0-9]{3}\d{2}\d{5}$/.test(v);
};

// Formatea a CC-XXX-YY-NNNNN (solo para display)
const formatISRC = (raw = "") => {
  const v = normalizeISRC(raw);
  if (v.length !== 12) return raw;
  return `${v.slice(0,2)}-${v.slice(2,5)}-${v.slice(5,7)}-${v.slice(7,12)}`;
};

const normalizeUPC = (raw = "") => String(raw).replace(/\D/g, "").trim();

const isValidUPC = (raw = "") => /^\d{12}$/.test(normalizeUPC(raw));

const getSelectedFormatName = (formatId, optsFormatos = []) => {
  const found = optsFormatos.find((o) => String(o.id) === String(formatId));
  return normalizeText(found?.name || found?.nombre || found?.title || "");
};

const getCodigoTipoByFormato = (formatId, optsFormatos = []) => {
  const name = getSelectedFormatName(formatId, optsFormatos);

  if (name.includes("album")) return "UPC";
  if (name.includes("single") || name.includes("singe") || name.includes("video")) return "ISRC";

  return "";
};

const isValidCodigoByFormato = (codigo, formatId, optsFormatos) => {
  const tipo = getCodigoTipoByFormato(formatId, optsFormatos);

  if (tipo === "ISRC") return isValidISRC(codigo);
  if (tipo === "UPC") return isValidUPC(codigo);

  return false;
};

const getCodigoHelperByFormato = (formatId, optsFormatos) => {
  const tipo = getCodigoTipoByFormato(formatId, optsFormatos);

  if (tipo === "ISRC") return "Formato ISRC: CC-XXX-YY-NNNNN";
  if (tipo === "UPC") return "Formato UPC: exactamente 12 dígitos";
  return "Selecciona primero un formato";
};

const getCodigoLabelByFormato = (formatId, optsFormatos) => {
  const tipo = getCodigoTipoByFormato(formatId, optsFormatos);

  if (tipo === "UPC") return "UPC (12 dígitos)";
  if (tipo === "ISRC") return "ISRC";

  return "Código";
};

const normalizeCodigoByFormato = (codigo, formatId, optsFormatos) => {
  const tipo = getCodigoTipoByFormato(formatId, optsFormatos);
  if (tipo === "ISRC") return normalizeISRC(codigo);
  if (tipo === "UPC") return normalizeUPC(codigo);
  return String(codigo || "").trim();
};

/* ====== Helpers para autocomplete remoto / local ====== */
const normalizeCatalog = (res) => {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.rows)) return res.rows;
  if (Array.isArray(res?.page)) return res.page;
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.results)) return res.results;
  return [];
};

const unwrapList = (data) =>
  Array.isArray(data) ? data
  : Array.isArray(data?.data) ? data.data
  : Array.isArray(data?.page) ? data.page
  : Array.isArray(data?.results) ? data.results
  : [];

function debounceFn(fn, ms = 300) {
  let t = null;
  return (...args) => {
    if (t) clearTimeout(t);
    t = setTimeout(() => fn(...args), ms);
  };
}

async function fetchPagedOptions({
  endpoint,
  q = "",
  page = 0,
  pageSize = 20,
  nameKey = "name",
  extraQuery = {},
}) {
  const params = { __limit: pageSize, __offset: page * pageSize, ...(extraQuery || {}) };
  if (q.trim()) params[`${nameKey}[$contains]`] = q.trim();
  const { data } = await api.get(endpoint, { params });
  const list = unwrapList(data);
  return (list || [])
    .map((x) => ({
      id: String(x.id ?? x._id ?? ""),
      name: x[nameKey] ?? x.nombre ?? x.title ?? `#${x.id ?? x._id ?? ""}`,
    }))
    .filter((o) => o.id);
}


/* ====== Componentes reutilizables: Single & Multi con label clickeable ====== */

function ClickableAutoSelect({
  id,
  label,
  value,                 // string id seleccionado
  onChange,              // (id) => void
  endpoint,              // ej. "/format" — si es falsy, modo LOCAL
  nameKey = "name",
  placeholder = "Selecciona una opción",
  required = false,
  disabled = false,
  fullWidth = true,
  size = "small",
  initialOptions = [],   // [{id,name}]
  extraMenuProps,        // opcional
  extraQuery,
}) {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [opts, setOpts] = useState(
    initialOptions.map(o => ({
      id: String(o.id),
      name: o.name ?? o.nombre ?? o.title ?? String(o.id),
    }))
  );
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);

  const localMode = !endpoint;

  useEffect(() => {
    // Si cambian las opciones iniciales (ej. llegaron grupos del backend),
    // actualiza opts para que el value ya tenga "name" y se muestre.
    setOpts(
      (initialOptions || []).map((o) => ({
        id: String(o.id),
        name: o.name ?? o.nombre ?? o.title ?? String(o.id),
      }))
    );
  }, [JSON.stringify(initialOptions || [])]);


  useEffect(() => {
    if (localMode) return;

    // Si cambia el filtro (ej. group_id), reiniciamos para que no mezcle resultados viejos
    setOpts([]);
    setPage(0);
    setHasMore(true);
    setInput("");
  }, [localMode, JSON.stringify(extraQuery || {})]);

  const loadPage = useMemo(
    () =>
      debounceFn(async (q, p) => {
        if (localMode) return;
        setLoading(true);
        try {
          const res = await fetchPagedOptions({ endpoint, q, page: p, pageSize: 20, nameKey, extraQuery });
          if (p === 0) setOpts(res);
          else setOpts((prev) => [...prev, ...res]);
          setHasMore(res.length >= 20);
          setPage(p);
        } finally {
          setLoading(false);
        }
      }, 250),
    [endpoint, nameKey, localMode, JSON.stringify(extraQuery || {})]
  );

  useEffect(() => {
    if (localMode || !open) return;
    loadPage(input, 0);
  }, [localMode, open, loadPage, input]);


  const openMenu = () => {
    if (!disabled) setOpen(true);
  };

  const handleOpen = () => {
    setOpen(true);
    if (localMode) {
      if (!opts.length && initialOptions.length) {
        setOpts(
          initialOptions.map(o => ({
            id: String(o.id),
            name: o.name ?? o.nombre ?? o.title ?? String(o.id),
          }))
        );
      }
      return;
    }
    if (!opts.length) loadPage("", 0);
  };

  const handleInput = (_e, val) => {
    setInput(val);
    if (localMode) {
      const base = initialOptions.map(o => ({
        id: String(o.id),
        name: o.name ?? o.nombre ?? o.title ?? String(o.id),
      }));
      if (!val.trim()) {
        setOpts(base);
      } else {
        const v = val.toLowerCase();
        setOpts(base.filter(o => o.name.toLowerCase().includes(v)));
      }
      setHasMore(false);
      setPage(0);
    } else {
      loadPage(val, 0);
    }
  };

  const onListScroll = (e) => {
    if (localMode) return;
    const n = e.currentTarget;
    if (hasMore && !loading && n.scrollTop + n.clientHeight >= n.scrollHeight - 8) {
      loadPage(input, page + 1);
    }
  };

  const selectedOption =
  opts.find((o) => String(o.id) === String(value)) ||
  (value ? { id: String(value), name: "Cargando..." } : null);


  return (
    <FormControl fullWidth={fullWidth} disabled={disabled}>
      {/* Label clickeable a todo lo ancho */}
      <Box
        role="button"
        tabIndex={0}
        onClick={openMenu}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            openMenu();
          }
        }}
        sx={{
          display: "block",
          width: "100%",
          cursor: disabled ? "default" : "pointer",
          color: "text.secondary",
          fontSize: "0.875rem",
          lineHeight: 1.25,
          mb: 0.5,
          userSelect: "none",
        }}
        aria-controls={id}
        aria-label={label}
      >
        {label}
      </Box>

      <Autocomplete
        id={id}
        size={size}
        disabled={disabled}
        open={open}
        onOpen={handleOpen}
        onClose={() => setOpen(false)}
        options={opts}
        loading={loading}
        value={selectedOption}
        onChange={(_e, opt) => onChange?.(opt?.id || "")}
        inputValue={input}
        onInputChange={handleInput}
        isOptionEqualToValue={(o, v) => o.id === v.id}
        getOptionLabel={(o) => o?.name || ""}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={!selectedOption ? (required ? `${placeholder} *` : placeholder) : ""}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        ListboxProps={{
          onScroll: onListScroll,
          style: { maxHeight: 320, overflow: "auto" },
          ...(extraMenuProps || {}),
        }}
      />
      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
        &nbsp;
      </Typography>
    </FormControl>
  );
}

function ClickableAutoMulti({
  id,
  label,
  value = [],           // array de ids (string)
  onChange,             // (ids[]) => void
  endpoint,
  nameKey = "name",
  placeholder = "Selecciona una o varias",
  fullWidth = true,
  size = "small",
  initialOptions = [],
  extraMenuProps,
  extraQuery,
}) {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [opts, setOpts] = useState(
    initialOptions.map(o => ({
      id: String(o.id),
      name: o.name ?? o.nombre ?? o.title ?? String(o.id),
    }))
  );
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);

  const localMode = !endpoint;

  const loadPage = useMemo(
    () =>
      debounceFn(async (q, p) => {
        if (localMode) return;
        setLoading(true);
        try {
          const res = await fetchPagedOptions({ endpoint, q, page: p, pageSize: 20, nameKey, extraQuery });
          if (p === 0) setOpts(res);
          else setOpts((prev) => [...prev, ...res]);
          setHasMore(res.length >= 20);
          setPage(p);
        } finally {
          setLoading(false);
        }
      }, 250),
    [endpoint, nameKey, localMode, JSON.stringify(extraQuery || {})]
  );


  const openMenu = () => setOpen(true);

  const handleOpen = () => {
    setOpen(true);
    if (localMode) {
      if (!opts.length && initialOptions.length) {
        setOpts(
          initialOptions.map(o => ({
            id: String(o.id),
            name: o.name ?? o.nombre ?? o.title ?? String(o.id),
          }))
        );
      }
      return;
    }
    if (!opts.length) loadPage("", 0);
  };

  const handleInput = (_e, val) => {
    setInput(val);
    if (localMode) {
      const base = initialOptions.map(o => ({
        id: String(o.id),
        name: o.name ?? o.nombre ?? o.title ?? String(o.id),
      }));
      if (!val.trim()) {
        setOpts(base);
      } else {
        const v = val.toLowerCase();
        setOpts(base.filter(o => o.name.toLowerCase().includes(v)));
      }
      setHasMore(false);
      setPage(0);
    } else {
      loadPage(val, 0);
    }
  };

  const onListScroll = (e) => {
    if (localMode) return;
    const n = e.currentTarget;
    if (hasMore && !loading && n.scrollTop + n.clientHeight >= n.scrollHeight - 8) {
      loadPage(input, page + 1);
    }
  };

  const selected = opts.filter((o) => value.map(String).includes(String(o.id)));

  return (
    <FormControl fullWidth={fullWidth}>
      <Box
        role="button"
        tabIndex={0}
        onClick={openMenu}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            openMenu();
          }
        }}
        sx={{
          display: "block",
          width: "100%",
          cursor: "pointer",
          color: "text.secondary",
          fontSize: "0.875rem",
          lineHeight: 1.25,
          mb: 0.5,
          userSelect: "none",
        }}
        aria-controls={id}
        aria-label={label}
      >
        {label}
      </Box>

      <Autocomplete
        id={id}
        multiple
        size={size}
        open={open}
        onOpen={handleOpen}
        onClose={() => setOpen(false)}
        options={opts}
        loading={loading}
        value={selected}
        onChange={(_e, arr) => onChange?.(arr.map((o) => String(o.id)))}
        inputValue={input}
        onInputChange={handleInput}
        isOptionEqualToValue={(o, v) => o.id === v.id}
        getOptionLabel={(o) => o?.name || ""}
        renderTags={(selected, getTagProps) =>
          selected.map((option, index) => (
            <Chip variant="outlined" label={option.name} {...getTagProps({ index })} />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={!selected.length ? placeholder : ""}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        ListboxProps={{
          onScroll: onListScroll,
          style: { maxHeight: 320, overflow: "auto" },
          ...(extraMenuProps || {}),
        }}
      />
      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
        &nbsp;
      </Typography>
    </FormControl>
  );
}

/* ============== Preview de archivos (local) ============== */
function FilePreview({ file }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!file) { setUrl(null); return; }
    const u = URL.createObjectURL(file);
    setUrl(u);
    return () => { URL.revokeObjectURL(u); };
  }, [file]);

  if (!file || !url) return null;

  const isImg = file.type.startsWith("image/");
  const isPdf = file.type === "application/pdf" || /\.pdf$/i.test(file.name);

  return (
    <Box sx={{ mt: 1.5 }}>
      {isImg && (
        <Box sx={{ border: "1px solid #eee", p: 1, borderRadius: 1, display: "inline-block" }}>
          <img
            src={url}
            alt={file.name}
            style={{ maxHeight: 160, maxWidth: "100%", display: "block" }}
          />
        </Box>
      )}
      {isPdf && (
        <Box sx={{ border: "1px solid #eee", p: 1, borderRadius: 1 }}>
          <object data={url} type="application/pdf" width="100%" height="220">
            <a href={url} target="_blank" rel="noreferrer">
              Ver PDF: {file.name}
            </a>
          </object>
        </Box>
      )}
      {!isImg && !isPdf && (
        <Typography variant="caption" color="text.secondary">
          Vista previa no disponible para “{file.name}”
        </Typography>
      )}
    </Box>
  );
}

/* Picker simple + chip (sin lógica de tamaño aquí) */
function FileField({ label, hint, accept, value, onChange, onClear, helperRight }) {
  const inputId = `file-${label.replace(/\s+/g, "-").toLowerCase()}`;
  return (
    <Card variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={1}>
        <Stack direction="row" justifyContent="space-between" alignItems="baseline">
          <Typography variant="subtitle1">{label}</Typography>
          {helperRight}
        </Stack>
        {hint && (
          <Typography variant="caption" color="text.secondary">
            {hint}
          </Typography>
        )}

        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <label htmlFor={inputId}>
            <input
              id={inputId}
              type="file"
              accept={accept}
              style={{ display: "none" }}
              onChange={(e) => onChange?.(e.target.files?.[0] ?? null)}
            />
            <Button color="dark" variant="contained" component="span">Seleccionar archivo</Button>
          </label>

          {value && (
            <Tooltip title={`${value.name} • ${(value.size/1024/1024).toFixed(2)} MB`}>
              <Chip
                color="info"
                variant="outlined"
                onDelete={onClear}
                label={<Box sx={ellipsis(260)}>{value.name}</Box>}
              />
            </Tooltip>
          )}
        </Stack>
      </Stack>
    </Card>
  );
}

/* ------------ componente ------------ */

export default function NuevaCertificacion() {
  const navigate = useNavigate();
  const { user, baseUser, isImpersonating } = useAuth();

  // form
  const [artist, setArtist] = useState("");
  const [title, setTitle] = useState("");
  const [album, setAlbum] = useState("");
  const [codigo, setCodigo] = useState("");
  const [codigoError, setCodigoError] = useState("");

  // ❌ award eliminado (lo calcula backend)
  const [nationality, setNationality] = useState("");  // ISO-3166-1 alpha-2 (MX/US…)
  const [releaseDate, setReleaseDate] = useState("");
  const [postAt, setPostAt] = useState("");
  const [saleUnits, setSaleUnits] = useState("");
  const [isPublic, setIsPublic] = useState(true);

  const [groupId, setGroupId] = useState("");
  
  const [formatId, setFormatId] = useState("");
  const [performerId, setPerformerId] = useState("");
  const [recordLabelId, setRecordLabelId] = useState("");
  const [recordTypes, setRecordTypes] = useState([]);  // ids
  const [genres, setGenres] = useState([]);            // ids

  // files
  const [labelFile, setLabelFile]   = useState(null);
  const [reportFile, setReportFile] = useState(null);
  const [coverFile, setCoverFile]   = useState(null);

  // catálogos
  const [optsFormatos, setOptsFormatos] = useState([]);
  const [optsCountries, setOptsCountries] = useState([]);
  const [optsGrupos, setOptsGrupos] = useState([]);
  const [optsPerformers, setOptsPerformers] = useState([]);
  const [optsSellos, setOptsSellos] = useState([]);
  const [optsRecordTypes, setOptsRecordTypes] = useState([]);
  const [optsGeneros, setOptsGeneros] = useState([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState([]);

  // cargar catálogos
  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true); setErrors([]);
      try {
        const [f, gnr, rt, gps, perf, lbls] = await Promise.all([
          listFormatos(),
          listGeneros(),
          listRecordTypes(),
          listGrupos({
            __limit: 1000,
            __offset: 0,
            __sort: { name: 1 },
          }),
          listPerformers(),
          listSellos({
            __limit: 1000,
            __offset: 0,
            __sort: { name: 1 },
          }),
        ]);
        if (cancel) return;
        setOptsFormatos(f);
        setOptsGeneros(gnr);
        setOptsRecordTypes(rt);
        setOptsCountries(getCountriesES());
        setOptsGrupos(normalizeCatalog(gps));
        setOptsSellos(normalizeCatalog(lbls));
        setOptsPerformers(perf);
      } catch (e) {
        if (!cancel) setErrors(parseErr(e));
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [user]);

  const isAdmin = isAdminUser(baseUser);
  const actingAsAdmin = isAdmin && !isImpersonating;
  const assignedGroupIds = useMemo(() => {
    if (Array.isArray(user?.groups) && user.groups.length) {
      return [
        ...new Set(
          user.groups
            .map((g) =>
              typeof g === "object" && g !== null
                ? g.id ?? g._id
                : g
            )
            .filter(
              (id) =>
                id !== null &&
                id !== undefined &&
                id !== ""
            )
            .map(String)
        ),
      ];
    }

    // compatibilidad temporal
    if (
      user?.group_id !== null &&
      user?.group_id !== undefined &&
      user?.group_id !== ""
    ) {
      return [String(user.group_id)];
    }

    return [];
  }, [user?.groups, user?.group_id]);
  const effectiveGroupId = String(groupId || "");
  const isSafari = useMemo(() => isSafariBrowser(), []);
  const releaseDateRef = useRef(null);
  const postAtRef = useRef(null);

  useEffect(() => {
    if (actingAsAdmin) return;

    if (!assignedGroupIds.length) {
      setGroupId("");
      return;
    }

    if (assignedGroupIds.length === 1) {
      const gid = assignedGroupIds[0];

      setGroupId((prev) =>
        prev === gid ? prev : gid
      );

      return;
    }

    setGroupId((prev) =>
      assignedGroupIds.includes(String(prev))
        ? prev
        : ""
    );
  }, [actingAsAdmin, assignedGroupIds]);

  useEffect(() => {
    setRecordLabelId("");
  }, [groupId]);

  useEffect(() => {
    setCodigo("");
    setCodigoError("");
  }, [formatId]);

  useEffect(() => {
    if (!isSafari) return;

    const clearIfStateEmpty = (ref, stateValue) => {
      const el = ref.current;
      if (!el) return;
      if (!stateValue && el.value) {
        el.value = "";
      }
    };

    const t = setTimeout(() => {
      clearIfStateEmpty(releaseDateRef, releaseDate);
      clearIfStateEmpty(postAtRef, postAt);
    }, 50);

    return () => clearTimeout(t);
  }, [isSafari, releaseDate, postAt]);

  const handleDateValue = (setter) => (e) => {
    const val = e?.target?.value || "";
    setter(val);
  };

  const clearSafariAutofillOnFocus = (ref, stateValue) => () => {
    if (!isSafari) return;
    const el = ref.current;
    if (!el) return;

    if (!stateValue && el.value) {
      el.value = "";
    }

    try {
      el.showPicker?.();
    } catch {}
  };

  const effectiveGroupName = useMemo(() => {
    if (!effectiveGroupId) return "";

    const found = optsGrupos.find(
      (g) =>
        String(g.id ?? g._id) ===
        String(effectiveGroupId)
    );

    return (
      found?.name ??
      found?.nombre ??
      found?.title ??
      `Grupo ${effectiveGroupId}`
    );
  }, [effectiveGroupId, optsGrupos]);

  const filteredSellos = useMemo(() => {
    if (!effectiveGroupId) {
      return [];
    }

    return (optsSellos || []).filter((label) => {
      const labelGroupIds =
        Array.isArray(label?.groups) &&
        label.groups.length
          ? label.groups
              .map((g) =>
                typeof g === "object" && g !== null
                  ? g.id ?? g._id
                  : g
              )
              .filter(
                (id) =>
                  id !== null &&
                  id !== undefined &&
                  id !== ""
              )
              .map(String)
          : label?.group_id != null
          ? [String(label.group_id)]
          : [];

      return labelGroupIds.includes(
        String(effectiveGroupId)
      );
    });
  }, [optsSellos, effectiveGroupId]);

  const isValid = useMemo(() => {
    const fechasValidas =
      isValidYMD(releaseDate) &&
      isValidYMD(postAt);

    return (
      artist.trim() &&
      title.trim() &&
      codigo.trim() &&
      formatId &&
      isValidCodigoByFormato(
        codigo,
        formatId,
        optsFormatos
      ) &&
      nationality &&
      fechasValidas &&
      effectiveGroupId &&
      recordLabelId &&
      labelFile &&
      reportFile &&
      coverFile
    );
  }, [
    artist,
    title,
    codigo,
    formatId,
    optsFormatos,
    nationality,
    releaseDate,
    postAt,
    effectiveGroupId,
    recordLabelId,
    labelFile,
    reportFile,
    coverFile,
  ]);

  // Validación de tamaño (front) para evitar 413
  const guardSize = (file, label) => {
    if (file && file.size > MAX_FILE_BYTES) {
      setErrors((prev) => [...prev, `El archivo de ${label} excede ${MAX_FILE_MB} MB`]);
      return false;
    }
    // limpia mensajes previos relacionados
    setErrors((prev) => prev.filter((m) => !m.includes(`de ${label} excede`)));
    return true;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!isValid) {
      const m = [];
      if (!artist.trim()) m.push("El artista es obligatorio.");
      if (!title.trim()) m.push("El título es obligatorio.");
      if (!formatId) m.push("El formato es obligatorio.");
      if (!codigo.trim()) m.push("El código es obligatorio.");
      if (!releaseDate) m.push("La fecha de lanzamiento es obligatoria.");
      if (releaseDate && !isValidYMD(releaseDate)) m.push("La fecha de lanzamiento no es válida. Usa el formato AAAA-MM-DD.");
      if (!nationality) m.push("La nacionalidad es obligatoria.");
      if (!postAt) m.push("La fecha de publicación (post_at) es obligatoria.");
      if (postAt && !isValidYMD(postAt)) m.push("La fecha de publicación no es válida. Usa el formato AAAA-MM-DD.");
      if (!labelFile || !reportFile || !coverFile) m.push("Faltan archivos por adjuntar. Carga sello, reporte y portada para poder guardar.");
      if (!effectiveGroupId) {
        m.push("El grupo es obligatorio.");
      }

      if (!recordLabelId) {
        m.push("El sello es obligatorio.");
      }
      setErrors([...new Set(m)]);
      return;
    }

    const codigoTipo = getCodigoTipoByFormato(formatId, optsFormatos);
    if (!codigoTipo) {
      setErrors(["No se pudo determinar el tipo de código para el formato seleccionado."]);
      return;
    }

    if (!isValidCodigoByFormato(codigo, formatId, optsFormatos)) {
      setErrors([
        codigoTipo === "ISRC"
          ? "El ISRC no es válido. Formato: CC-XXX-YY-NNNNN."
          : "El UPC no es válido. Debe contener exactamente 12 dígitos."
      ]);
      return;
    }

    // Validación extra: nacionalidad 2–3 letras
    const natCode = (nationality || "").toUpperCase().trim();
    if (!/^[A-Z]{2,3}$/.test(natCode)) {
      setErrors(["Selecciona una nacionalidad con código ISO válido (ej. MX, US)."]);
      return;
    }

    if (!labelFile || !reportFile || !coverFile) {
      setErrors(["Faltan archivos por adjuntar. Carga sello, reporte y portada para poder guardar."]);
      return;
    }

    if (!isValidYMD(releaseDate)) {
      setErrors(["La fecha de lanzamiento no es válida. Usa el formato AAAA-MM-DD."]);
      return;
    }

    if (!isValidYMD(postAt)) {
      setErrors(["La fecha de publicación no es válida. Usa el formato AAAA-MM-DD."]);
      return;
    }

    // Límite de tamaño (cliente)
    if (!guardSize(labelFile, "sello") || !guardSize(reportFile, "reporte") || !guardSize(coverFile, "portada")) {
      return;
    }

    setSaving(true); setErrors([]);
    try {
      // Mantiene compatibilidad con backend actual enviando el código normalizado en la llave isrc
      const fields = {
        artist: artist.trim(),
        title: title.trim(),
        album: album.trim() || undefined,
        isrc: normalizeCodigoByFormato(codigo, formatId, optsFormatos),
        nationality: natCode,
        release_date: isoDate(releaseDate),
        post_at: isoDate(postAt),
        sale_units: toNum(saleUnits),
        performer_id: toNum(performerId),
        format_id: toNum(formatId),
        record_label_id: toNum(recordLabelId),
        group_id: toNum(effectiveGroupId),
        is_public: !!isPublic,
        genres: toNumArray(genres),
        record_types: toNumArray(recordTypes),
      };

      // Limpia claves vacías/undefined para no mandar null/NaN
      Object.keys(fields).forEach((k) => (fields[k] === undefined ? delete fields[k] : null));

      const fd = toCertFormData(fields, {
        label:  labelFile || undefined,
        report: reportFile || undefined,
        cover:  coverFile || undefined,
      });

      await createCertificacion(fd);
      navigate("/certificaciones", { replace: true });
    } catch (e) {
      const msgs = parseErr(e);
      if (!msgs.length) {
        msgs.push("No se pudo guardar la certificación. Revisa los campos obligatorios o inténtalo nuevamente.");
      }
      setErrors(msgs);
    } finally {
      setSaving(false);
    }

  };

  return (
    <FormPage
      title="Nueva certificación"
      onBack={() => navigate("/certificaciones")}
      actions={
        <>
          <PrimaryButton type="submit" form="form-cert" disabled={!isValid || saving || loading}>
            {saving ? "Guardando..." : "Guardar"}
          </PrimaryButton>
          <OutlineButton onClick={() => navigate("/certificaciones")}>Cancelar</OutlineButton>
        </>
      }
    >
      {errors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errors.length === 1 ? (
            <span>{safeMsg(errors[0])}</span>
          ) : (
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {errors.map((m, i) => (
                <li key={i}>{safeMsg(m)}</li>
              ))}
            </ul>
          )}
        </Alert>
      )}

      <form id="form-cert" onSubmit={onSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <TextField label="Artista" value={artist} onChange={(e)=>setArtist(e.target.value)} fullWidth required />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField label="Título" value={title} onChange={(e)=>setTitle(e.target.value)} fullWidth required />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField label="Álbum" value={album} onChange={(e)=>setAlbum(e.target.value)} fullWidth />
          </Grid>

          {/* Formato */}
          <Grid item xs={12} md={3}>
            <ClickableAutoSelect
              id="format"
              value={String(formatId || "")}
              onChange={(id) => setFormatId(id)}
              endpoint="/format"
              nameKey="name"
              placeholder="Formato*"
              initialOptions={optsFormatos}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              label={getCodigoLabelByFormato(formatId, optsFormatos)}
              value={codigo}
              onChange={(e) => {
                const raw = e.target.value;
                const tipo = getCodigoTipoByFormato(formatId, optsFormatos);

                let val = raw;
                if (tipo === "ISRC") {
                  val = raw.toUpperCase().replace(/[^A-Z0-9\- ]/g, "");
                } else if (tipo === "UPC") {
                  val = raw.replace(/\D/g, "").slice(0, 12);
                }

                setCodigo(val);

                if (!val.trim()) {
                  setCodigoError("");
                  return;
                }

                if (!tipo) {
                  setCodigoError("Selecciona primero un formato.");
                  return;
                }

                if (tipo === "ISRC") {
                  setCodigoError(
                    isValidISRC(val) ? "" : "Formato inválido. Usa CC-XXX-YY-NNNNN."
                  );
                } else if (tipo === "UPC") {
                  setCodigoError(
                    isValidUPC(val) ? "" : "El UPC debe tener exactamente 12 dígitos."
                  );
                }
              }}
              onBlur={() => {
                const tipo = getCodigoTipoByFormato(formatId, optsFormatos);
                if (tipo === "ISRC" && isValidISRC(codigo)) {
                  setCodigo(formatISRC(codigo));
                }
                if (tipo === "UPC" && codigo) {
                  setCodigo(normalizeUPC(codigo));
                }
              }}
              fullWidth
              required
              disabled={!formatId}
              error={!!codigoError}
              helperText={codigoError || (getCodigoTipoByFormato(formatId, optsFormatos) === "UPC" ? `${codigo.length}/12 dígitos` : getCodigoHelperByFormato(formatId, optsFormatos))}
              inputProps={{
                maxLength: getCodigoTipoByFormato(formatId, optsFormatos) === "UPC" ? 12 : 15,
              }}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <ClickableAutoSelect
              id="nationality"
              label="Nacionalidad*"
              value={nationality}
              onChange={(code) => setNationality((code || "").toUpperCase())}
              endpoint={null}
              placeholder="Selecciona nacionalidad"
              required
              initialOptions={optsCountries.map(c => ({
                id: c.code,
                name: `${c.name} (${c.code})`,
              }))}
            />
          </Grid>


          {/* Intérprete */}
          <Grid item xs={12} md={3}>
            <ClickableAutoSelect
              id="performer"
              label="Intérprete*"
              value={String(performerId || "")}
              onChange={(id) => setPerformerId(id)}
              endpoint="/performer"
              nameKey="name"
              placeholder="Selecciona intérprete"
              initialOptions={optsPerformers}
            />
          </Grid>

          {/* Sello */}
          <Grid item xs={12} md={3}>
            <ClickableAutoSelect
              id="label"
              label="Sello*"
              value={String(recordLabelId || "")}
              onChange={(id) => setRecordLabelId(id)}
              endpoint={null}
              nameKey="name"
              placeholder={
                effectiveGroupId
                  ? "Selecciona sello"
                  : "Selecciona primero un grupo"
              }
              initialOptions={filteredSellos}
              disabled={!effectiveGroupId}
            />
          </Grid>

          {/* Grupo */}
          <Grid item xs={12} md={4}>
            {actingAsAdmin ? (
              <ClickableAutoSelect
                id="group"
                label="Grupo*"
                value={String(groupId || "")}
                onChange={(id) => setGroupId(id)}
                endpoint="/group"
                nameKey="name"
                placeholder="Selecciona grupo"
                initialOptions={optsGrupos}
              />
            ) : assignedGroupIds.length > 1 ? (
              <ClickableAutoSelect
                id="group"
                label="Grupo*"
                value={String(groupId || "")}
                onChange={(id) => setGroupId(id)}
                endpoint={null}
                nameKey="name"
                placeholder="Selecciona grupo"
                initialOptions={optsGrupos.filter((g) =>
                  assignedGroupIds.includes(
                    String(g.id ?? g._id)
                  )
                )}
              />
            ) : (
              <TextField
                label="Grupo*"
                value={effectiveGroupName}
                fullWidth
                disabled
              />
            )}
          </Grid>

          {/* Géneros (multi) */}
          <Grid item xs={12} md={4}>
            <ClickableAutoMulti
              id="genres"
              label="Géneros*"
              value={genres.map(String)}
              onChange={(ids) => setGenres(ids)}
              endpoint="/genre"
              nameKey="name"
              placeholder="Selecciona géneros"
              initialOptions={optsGeneros}
            />
          </Grid>

          {/* Grabación (record types) (multi) */}
          <Grid item xs={12} md={4}>
            <ClickableAutoMulti
              id="recordTypes"
              label="Grabación*"
              value={recordTypes.map(String)}
              onChange={(ids) => setRecordTypes(ids)}
              endpoint="/recordType"
              nameKey="name"
              placeholder="Selecciona tipo(s) de grabación"
              initialOptions={optsRecordTypes}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              label="Unidades"
              type="number"
              value={saleUnits}
              onChange={(e)=>setSaleUnits(e.target.value)}
              fullWidth
              inputProps={{ min: 0 }}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              inputRef={releaseDateRef}
              label="Fecha de lanzamiento*"
              type="date"
              value={releaseDate || ""}
              onChange={handleDateValue(setReleaseDate)}
              onInput={handleDateValue(setReleaseDate)}
              onFocus={clearSafariAutofillOnFocus(releaseDateRef, releaseDate)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              required
              inputProps={{
                autoComplete: "off",
              }}
              helperText="Selecciona la fecha desde el calendario"
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              inputRef={postAtRef}
              label="Publicación (post_at)*"
              type="date"
              value={postAt || ""}
              onChange={handleDateValue(setPostAt)}
              onInput={handleDateValue(setPostAt)}
              onFocus={clearSafariAutofillOnFocus(postAtRef, postAt)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              required
              inputProps={{
                autoComplete: "off",
              }}
              helperText="Selecciona la fecha desde el calendario"
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControlLabel
              control={<Switch checked={!!isPublic} onChange={(e)=>setIsPublic(e.target.checked)} />}
              label="Público"
            />
          </Grid>

          <Grid item xs={12}><Divider /></Grid>

          {/* Aviso global de tamaño */}
          <Grid item xs={12}>
            <Alert severity="info" sx={{ mb: 0 }}>
              Límite de tamaño por archivo: <strong>{MAX_FILE_MB} MB</strong>. Para PDFs usa compresión si es necesario.
            </Alert>
          </Grid>

          {/* Files con validación y preview */}
          <Grid item xs={12} md={6}>
            <FileField
              label="Archivo sello (PDF)*"
              hint={`PDF obligatorio (≤ ${MAX_FILE_MB} MB)`}
              accept="application/pdf"
              value={labelFile}
              onChange={(f) => { if (guardSize(f, "sello")) setLabelFile(f); }}
              onClear={()=>setLabelFile(null)}
              helperRight={labelFile && <Typography variant="caption">{(labelFile.size/1024/1024).toFixed(2)} MB</Typography>}
            />
            <FilePreview file={labelFile} />
          </Grid>

          <Grid item xs={12} md={6}>
            <FileField
              label="Archivo reporte (PDF)*"
              hint={`PDF obligatorio (≤ ${MAX_FILE_MB} MB)`}
              accept="application/pdf"
              value={reportFile}
              onChange={(f) => { if (guardSize(f, "reporte")) setReportFile(f); }}
              onClear={()=>setReportFile(null)}
              helperRight={reportFile && <Typography variant="caption">{(reportFile.size/1024/1024).toFixed(2)} MB</Typography>}
            />
            <FilePreview file={reportFile} />
          </Grid>

          <Grid item xs={12} md={6}>
            <FileField
              label="Portada (imagen)*"
              hint={`Imagen obligatoria (≤ ${MAX_FILE_MB} MB)`}
              accept="image/*"
              value={coverFile}
              onChange={(f) => { if (guardSize(f, "portada")) setCoverFile(f); }}
              onClear={()=>setCoverFile(null)}
              helperRight={coverFile && <Typography variant="caption">{(coverFile.size/1024/1024).toFixed(2)} MB</Typography>}
            />
            <FilePreview file={coverFile} />
          </Grid>
        </Grid>
      </form>
    </FormPage>
  );
}
